import { CSSProperties, WheelEvent } from "react";
import { MapStateToProps, Selector } from "react-redux";

import { CONST_ROOT_COMPONENT_INDEX, CONST_SCALE_STEP, PICASSO_PAINTING_CSS_CLASS } from "./constants";
import { AlignType, AnchorPoint, ResizeOptions } from "./enums";
import { IComponent, IPoint } from "./interfaces";
import { IPicassoState, ScrollPosition } from "./types";

/**
 * Helper function that takes care of wrapping the external mapStateToProps function so that the condition that Picasso shouldn't be dependent to Multiweb is met.
 * @param picassoStateSelector Selector for getting the state of Picasso.
 * @param picassoInstanceKey Key of specific instance of Picasso in application.
 * @returns Wrap function that enables components of picasso to access it's redux state.
 */
export const createWrapPicassoMapStateToProps = <AS>(
    picassoStateSelector: Selector<AS, IPicassoState, string>,
    picassoInstanceKey: string,
) => <SP, OP>(mapStateToProps: MapStateToProps<SP, OP, IPicassoState>): MapStateToProps<SP, OP, AS> =>
        (state: AS, ownProps: OP) => mapStateToProps(picassoStateSelector(state, picassoInstanceKey), ownProps);

/**
 * Určuje, zdali je poskytnuté číslo v ostrém intervalu dvou čísel.
 * @param x Číslo k porovnání vůči minimu a maximu.
 * @param min Minimální možná hodnota čísla.
 * @param max Maximální možná hodnota čísla.
 */
export const isNumberBetween = (x: number, min: number, max: number): boolean => x >= min && x <= max;

/**
 * Omezí číslo do zadaného uzavřeného intervalu.
 */
export const clamp = (value: number, lower: number, upper: number) => Math.max(lower, Math.min(value, upper));

/**
 * Vrátí největší možné celé číslo, které je násobekem @param gridIndex a zároveň je menší než @param rawDelta
 * @param rawDelta Skutečná vzdálenost mezi dvěma body.
 * @param gridIndex Velikost mezer ve gridu.
 */
export const gridifyDelta = (rawDelta: number, gridIndex: number): number => {
    const roundedRawDelta = Math.round(rawDelta);

    if (gridIndex === 1 || !roundedRawDelta)
        return roundedRawDelta;

    const indexInNumberLeft = roundedRawDelta > 0 ? Math.floor(roundedRawDelta / gridIndex) : Math.ceil(roundedRawDelta / gridIndex);

    return indexInNumberLeft * gridIndex;
};

export const checkNewWidthAndLeft = (newWidth: number, newLeft: number, deltaLeft: number, component: IComponent): { width: number; left: number } => {
    if (newWidth < component.minWidth) {
        if (deltaLeft === 0)
            return { width: component.minWidth, left: component.relativeLeft };

        return { width: component.minWidth, left: component.relativeLeft + (component.width - component.minWidth) };
    }

    return { width: newWidth, left: newLeft };
};

export const checkNewHeightAndTop = (newHeight: number, newTop: number, deltaTop: number, component: IComponent): { height: number; top: number } => {
    if (newHeight < component.minHeight) {
        if (deltaTop === 0)
            return { height: component.minHeight, top: component.relativeTop };

        return { height: component.minHeight, top: component.relativeTop + (component.height - component.minHeight) };
    }

    return { height: newHeight, top: newTop };
};

/**
 * Vrací nové hodnoty pozice scrollů Picasso plátna pro případ, že došlo k zoomu.
 * @param e Parametry WheelEventu.
 * @param picassoPaintingEl Element plátna picassa.
 * @param scaleIndex Aktuální scaleIndex plátna.
 * @param newScaleIndex ScaleIndex, který bude nastaven po současném wheel eventu.
 */
export const getNewPaintingScrollPositionOnZoom = (e: WheelEvent, picassoPaintingEl: HTMLElement, scaleIndex: number, newScaleIndex: number): ScrollPosition => {
    const { left, top } = e.currentTarget.getBoundingClientRect();

    const { left: leftScrollPosition, top: topScrollPosititon } = getCenteredPaintingScrollPosition(picassoPaintingEl);
    const distanceX = e.clientX - leftScrollPosition - left;
    const distanceY = e.clientY - topScrollPosititon - top;

    const xs = (distanceX + Math.round(picassoPaintingEl.scrollLeft)) / scaleIndex; // vzdálenost kurzoru od levé hrany root komponenty
    const ys = (distanceY + Math.round(picassoPaintingEl.scrollTop)) / scaleIndex; // vzdálenost kurzoru od horní hrany root komponenty

    const newScrollLeft = (xs * newScaleIndex) - distanceX;
    const newScrollTop = (ys * newScaleIndex) - distanceY;

    return { left: Math.round(newScrollLeft), top: Math.round(newScrollTop) };
};

/**
 * Vrací nové hodnoty pozice scrollů Picasso plátna pro případ, že došlo pouze k posunu plátna, ne zoomu.
 * @param param0 Iniciální bod, od kterého se pan-uje.
 * @param e Událost mouse move.
 * @returns Novou pozici scroll barů.
 */
export const getNewPaintingScrollPrositionOnPan = ({ x, y }: IPoint, e: React.MouseEvent): ScrollPosition => ({ left: x - e.clientX, top: y - e.clientY });

/**
 * Vrací novou hodnotu scale indexu (přiblížení).
 * @param e Parametry události kolečka myši.
 * @param scaleIndex Současný scale index.
 */
export const getNewScaleIndex = (e: WheelEvent, scaleIndex: number) => {
    if (e.deltaY === 0) {
        return scaleIndex;
    }

    return Math.round((e.deltaY < 0 ? scaleIndex + CONST_SCALE_STEP : scaleIndex - CONST_SCALE_STEP) * 10) / 10;
};

/**
 * Vrací styl určující viditelnost kotev pro resizování picasso komponenty.
 * @param anchorPoint Směr/pozice kotvy.
 * @param uiComponent Komponenta picassa.
 * @returns Objekt CSSProperties.
 */
export const getAnchorStyle = (anchorPoint: AnchorPoint, uiComponent: IComponent): CSSProperties => {
    const { resizeOption, align } = uiComponent;
    const isVertical = () => (resizeOption & ResizeOptions.vertical) === ResizeOptions.vertical;
    const isHorizontal = () => (resizeOption & ResizeOptions.horizontal) === ResizeOptions.horizontal;

    if (anchorPoint === AnchorPoint.N)
        return { visibility: isVertical() && (align === AlignType.bottom || align === AlignType.none) ? undefined : "hidden" };

    if (anchorPoint === AnchorPoint.E)
        return { visibility: isHorizontal() && (align === AlignType.left || align === AlignType.none) ? undefined : "hidden" };

    if (anchorPoint === AnchorPoint.S)
        return { visibility: isVertical() && (align === AlignType.top || align === AlignType.none) ? undefined : "hidden" };

    if (anchorPoint === AnchorPoint.W)
        return { visibility: isHorizontal() && (align === AlignType.right || align === AlignType.none) ? undefined : "hidden" };

    if (anchorPoint === AnchorPoint.NE || anchorPoint === AnchorPoint.SE || anchorPoint === AnchorPoint.SW || anchorPoint === AnchorPoint.NW)
        return { display: resizeOption === ResizeOptions.both && (align === AlignType.none) ? undefined : "none" };

    throw new Error(`[PICASSO] - Anchor point of type '${anchorPoint}' is not implemented!`);
};

/**
 * Vrácí aktualizované CSS vlastnosti nastavující poziční a rozměrové vlastnosti komponenty vůči zakotvení.
 * @param uiComponent Konkrétní komponenta picassa.
 * @returns Objekt aktualizovaných CSS vlastností.
 */
export const getDimensionsOfComponent = (fillsParent: boolean, uiComponent: IComponent, rowNumber: number, columnNumber: number): CSSProperties => {
    if (fillsParent)
        return { position: "relative", height: "100%", width: "100%" };

    if (uiComponent.align === AlignType.none)
        return {
            left: uiComponent.relativeLeft,
            top: uiComponent.relativeTop,
            height: uiComponent.noCssHeight ? undefined : uiComponent.height,
            width: uiComponent.noCssWidth ? undefined : uiComponent.width,
        };

    if (uiComponent.align === AlignType.left || uiComponent.align === AlignType.right)
        return {
            position: "relative",
            gridArea: `${rowNumber}/${columnNumber}/${rowNumber + 1}/${columnNumber + 1}`,
            width: uiComponent.width,
        };

    if (uiComponent.align === AlignType.top || uiComponent.align === AlignType.bottom)
        return {
            position: "relative",
            gridArea: `${rowNumber}/1/${rowNumber + 1}/${columnNumber}`,
            height: uiComponent.noCssHeight ? undefined : uiComponent.height,
        };

    return {
        position: "relative",
        gridArea: `${rowNumber}/${columnNumber}/${rowNumber + 1}/${columnNumber + 1}`,
    };
};

/**
 * Smaže vybrané komponenty z pole komponent picassa a zbylým komponentám aktualizuje reference a indexy.
 * @param mutablePicassoComponents Mutovatelné pole komponent picassa.
 * @param componentIndexesToDelete Seznam indexů komponent ke smazání.
 * @returns Updatovaný readonly seznam komponent.
 */
export const deletePicassoComponents = (mutablePicassoComponents: IComponent[], componentIndexesToDelete: readonly number[]): readonly IComponent[] => {
    const orderedComponentsToDelete = [...componentIndexesToDelete].sort((a, b) => a - b);
    const parentId = mutablePicassoComponents[orderedComponentsToDelete[0]].parent;
    let offset = 0;

    for (const componentIndex of orderedComponentsToDelete) {
        const actualIndex = componentIndex + offset; // Když odebírám prvky z pole, tak předchozí index ztrácí smyls, proto vyrovnávám pomocí offsetu.

        for (let i = 0; i < mutablePicassoComponents.length; i++) {
            const picassoComponent = mutablePicassoComponents[i];
            const parentIndex = picassoComponent.parent ?? CONST_ROOT_COMPONENT_INDEX;

            mutablePicassoComponents[i] = {
                ...picassoComponent,
                index: (i > actualIndex) ? picassoComponent.index - 1 : picassoComponent.index,
                parent: (parentIndex > actualIndex) ? parentIndex - 1 : picassoComponent.parent,
                children: picassoComponent.children.filter(child => child !== actualIndex).map(child => child > actualIndex ? child - 1 : child),
            };
        }

        offset--;

        const deletedComponent: IComponent = { ...mutablePicassoComponents[actualIndex], index: Number.MAX_VALUE }; // Musím nastavit neexistující index.
        mutablePicassoComponents.splice(actualIndex, 1);

        if (parentId === deletedComponent.parent && deletedComponent.align !== AlignType.none)
            updateSiblings(deletedComponent, mutablePicassoComponents, -deletedComponent.width, -deletedComponent.height);
    }

    return mutablePicassoComponents;
};

/**
 * Vrací nové relativní souřadnice komponenty, podle nového zakotvení.
 * @param align Nové zakotvení picasso komponenty.
 * @param selectedComp Zvolená komponenta.
 * @param parentComp Rodičovská komponenta zvolené komponenty.
 * @returns Objekt obsaující nové souřadnice.
 */
export const getNewCoordinates = (align: AlignType, selectedComp: IComponent, parentComp: IComponent, siblingComps: readonly IComponent[]): Pick<IComponent, "relativeLeft" | "relativeTop" | "height" | "width"> => {
    const siblingsWithSameAlign = siblingComps.filter(x => x.align === align);
    const siblingsWithoutMe = siblingComps.filter(x => x.index !== selectedComp.index);

    if (align === AlignType.client)
        return {
            relativeLeft: siblingsWithoutMe.filter(x => x.align === AlignType.left).reduce((x, y) => x + y.width, 0),
            relativeTop: siblingsWithoutMe.filter(x => x.align === AlignType.top).reduce((x, y) => x + y.height, 0),
            height: parentComp.height - (siblingsWithoutMe.filter(x => x.align === AlignType.top || x.align === AlignType.bottom).reduce((x, y) => x + y.height, 0)),
            width: parentComp.width - (siblingsWithoutMe.filter(x => x.align === AlignType.left || x.align === AlignType.right).reduce((x, y) => x + y.width, 0)),
        };

    if (align === AlignType.right)
        return {
            relativeLeft: parentComp.width - (selectedComp.width + siblingsWithSameAlign.reduce((x, y) => x + y.width, 0)),
            relativeTop: siblingsWithoutMe.filter(x => x.align === AlignType.top).reduce((x, y) => x + y.height, 0),
            height: parentComp.height - (siblingsWithoutMe.filter(x => x.align === AlignType.top || x.align === AlignType.bottom).reduce((x, y) => x + y.height, 0)),
            width: selectedComp.width,
        };

    if (align === AlignType.left)
        return {
            relativeLeft: siblingsWithSameAlign.reduce((x, y) => x + y.width, 0),
            relativeTop: siblingsWithoutMe.filter(x => x.align === AlignType.top).reduce((x, y) => x + y.height, 0),
            height: parentComp.height - (siblingsWithoutMe.filter(x => x.align === AlignType.top || x.align === AlignType.bottom).reduce((x, y) => x + y.height, 0)),
            width: selectedComp.width,
        };

    if (align === AlignType.bottom)
        return {
            relativeLeft: 0,
            relativeTop: parentComp.height - (selectedComp.height + siblingsWithSameAlign.reduce((x, y) => x + y.height, 0)),
            height: selectedComp.height,
            width: parentComp.width,
        };

    if (align === AlignType.top)
        return {
            relativeLeft: 0,
            relativeTop: siblingsWithSameAlign.reduce((x, y) => x + y.height, 0),
            height: selectedComp.height,
            width: parentComp.width,
        };

    return { // AKA AlNone
        relativeLeft: selectedComp.relativeLeft,
        relativeTop: selectedComp.relativeTop,
        height: selectedComp.height,
        width: selectedComp.width,
    };
};

/**
 * Rekurzivně updatuje rozměry a pozice stromu komponent, které mají align různo od none.
 * @param children Seznam indexů uzlů aktuální úrovně rekurze.
 * @param mutableComponents Mutable seznam picasso komponent.
 * @param deltaWidth Rozdíl šířky kořenové komponenty.
 * @param deltaHeight Rozdíl výšky kořenové komponenty.
 */
export const updateSubtreeRecursively = (children: readonly number[], mutableComponents: IComponent[], deltaWidth: number, deltaHeight: number): void => {
    if (children.length === 0 || (deltaWidth === 0 && deltaHeight === 0))
        return;

    for (const childIndex of children) {
        const child = mutableComponents[childIndex];

        if (child.align === AlignType.none) {
            continue;
        } else if (child.align === AlignType.top && deltaWidth !== 0) {
            mutableComponents[child.index] = { ...child, width: child.width + deltaWidth };
            updateSubtreeRecursively(child.children, mutableComponents, deltaWidth, 0);
        } else if (child.align === AlignType.left && deltaHeight !== 0) {
            mutableComponents[child.index] = { ...child, height: child.height + deltaHeight };
            updateSubtreeRecursively(child.children, mutableComponents, 0, deltaHeight);
        } else if (child.align === AlignType.right) {
            mutableComponents[child.index] = { ...child, height: child.height + deltaHeight, relativeLeft: child.relativeLeft + deltaWidth };
            if (deltaHeight !== 0)
                updateSubtreeRecursively(child.children, mutableComponents, 0, deltaHeight);
        } else if (child.align === AlignType.bottom) {
            mutableComponents[child.index] = { ...child, width: child.width + deltaWidth, relativeTop: child.relativeTop + deltaHeight };
            if (deltaWidth !== 0)
                updateSubtreeRecursively(child.children, mutableComponents, deltaWidth, 0);
        } else if (child.align === AlignType.client) {
            mutableComponents[child.index] = { ...child, width: child.width + deltaWidth, height: child.height + deltaHeight };
            updateSubtreeRecursively(child.children, mutableComponents, deltaWidth, deltaHeight);
        }
    }
};

export const updateSiblings = (component: IComponent, mutableComponents: IComponent[], deltaWidth: number, deltaHeight: number): void => {
    const parentComp = mutableComponents[component.parent ?? CONST_ROOT_COMPONENT_INDEX];
    const siblings = parentComp.children.map(x => mutableComponents[x]);

    if (component.align !== AlignType.none)
        updateRelativeDistances(component, siblings, mutableComponents, deltaWidth, deltaHeight);

    const newHeight = parentComp.height - siblings.filter(x => x.align === AlignType.top || x.align === AlignType.bottom).reduce((x, y) => x + y.height, 0);
    const newRelativeTop = siblings.filter(x => x.align === AlignType.top).reduce((x, y) => x + y.height, 0);
    const alClientNewWidth = parentComp.width - siblings.filter(x => x.align === AlignType.left || x.align === AlignType.right).reduce((x, y) => x + y.width, 0);
    const alClientNewRelativeLeft = siblings.filter(x => x.align === AlignType.left).reduce((x, y) => x + y.width, 0);

    for (const sibling of siblings.filter(x => (x.align === AlignType.left || x.align === AlignType.right || x.align === AlignType.client) && component.index !== x.index)) {
        const updatedComp = mutableComponents[sibling.index];
        mutableComponents[sibling.index] = {
            ...updatedComp,
            height: newHeight,
            relativeTop: newRelativeTop,
            width: updatedComp.align === AlignType.client ? alClientNewWidth : updatedComp.width,
            relativeLeft: updatedComp.align === AlignType.client ? alClientNewRelativeLeft : updatedComp.relativeLeft,
        };

        updateSubtreeRecursively(updatedComp.children, mutableComponents, mutableComponents[sibling.index].width - updatedComp.width, mutableComponents[sibling.index].height - updatedComp.height);
    }

    if (component.index !== Number.MAX_VALUE) // Když mažu komponentu, tak nemá cenu updatovat její potomky
        updateSubtreeRecursively(component.children, mutableComponents, mutableComponents[component.index].width - component.width, mutableComponents[component.index].height - component.height);
};

/**
 * Updatuje relativní vzdálenost komponent, které mají stejný align, jako právě resizovaná komponenta. Je to potřeba například v situaci,
 * kdy mám 3 komponenty vedle sebe s align="alLeft" a zvětšuji prostřední. První zůstané netknutá a poslední musím zvětšit relativní vzdálenost od levého kraje.
 * @param component Komponenta se kterou hýbu.
 * @param siblings Sourozenci resizované komponenty.
 * @param updatedComponents Mutable kolekce všech komponent picassa.
 * @param deltaWidth Rozdíl šířky resizované komponenty oproti poslednímu stavu.
 * @param deltaHeight Rozdíl výšky resizované komponenty oproti poslednímu stavu.
 */
const updateRelativeDistances = (component: IComponent, siblings: readonly IComponent[], updatedComponents: IComponent[], deltaWidth: number, deltaHeight: number): void => {
    for (const sibling of siblings.filter(x => x.align === component.align && component.index !== x.index)) {
        if (component.align === AlignType.top && (component.relativeTop + component.height) <= sibling.relativeTop)
            updatedComponents[sibling.index] = { ...sibling, relativeTop: sibling.relativeTop + deltaHeight };
        else if (component.align === AlignType.left && (component.relativeLeft + component.width) <= sibling.relativeLeft)
            updatedComponents[sibling.index] = { ...sibling, relativeLeft: sibling.relativeLeft + deltaWidth };
        else if (component.align === AlignType.right && (sibling.relativeLeft + sibling.width) <= component.relativeLeft)
            updatedComponents[sibling.index] = { ...sibling, relativeLeft: sibling.relativeLeft - deltaWidth };
        else if (component.align === AlignType.bottom && (sibling.relativeTop + sibling.height) <= component.relativeTop)
            updatedComponents[sibling.index] = { ...sibling, relativeTop: sibling.relativeTop - deltaHeight };
    }
};

export const getCenteredPaintingScrollPosition = (picassoPaintingEl: HTMLElement | undefined | null): ScrollPosition => ({
    left: (picassoPaintingEl?.clientWidth ?? window.innerWidth) - 100,
    top: (picassoPaintingEl?.clientHeight ?? window.innerHeight) - 100,
});

export const getPaintingDimensions = (picassoPaintingEl: HTMLElement | undefined | null, rootComponent: IComponent | undefined, scaleIndex: number) => {
    const rootComponentWidth = rootComponent ? (rootComponent.width + rootComponent.relativeLeft) : 0;
    const rootComponentHeight = rootComponent ? (rootComponent.height + rootComponent.relativeTop) : 0;

    const { left, top } = getCenteredPaintingScrollPosition(picassoPaintingEl);
    const width = (left * 2 + rootComponentWidth) + (((rootComponentWidth * scaleIndex) - rootComponentWidth));
    const height = (top * 2 + rootComponentHeight) + (((rootComponentHeight * scaleIndex) - rootComponentHeight));

    return { width: `${width}px`, height: `${height}px` };
};

/**
 * Vrací aktuální hodnotu pozice scrollbarů plítna Picasa. Hodnota má od sebe odečten margin,
 * takže nula odpovídá vycentrované pozici rootovské komponenty a ne skutečné nule na scrollbaru.
 * @param parentElement Element jehož potomkem má být plátno Picassa.
 * @returns Relativní pozici scrollu.
 */
export const getCurrentPaintingScrollPosition = (parentElement: HTMLElement): ScrollPosition => {
    const picassoPaintingEl = parentElement.querySelector(`.${PICASSO_PAINTING_CSS_CLASS}`);

    if (!picassoPaintingEl) {
        throw new Error("[PICASSO] - No instance of Picasso painting was found!");
    }

    const { left, top } = getCenteredPaintingScrollPosition(picassoPaintingEl as HTMLElement);

    return { left: picassoPaintingEl.scrollLeft - left, top: picassoPaintingEl.scrollTop - top };
};
